import { FailureApi } from "@smp/api";
import { UiModuleLoader } from "@smp/ui";
import {
  hashRot13,
  REACT_APP__INTERVAL_VERSION_CHECK,
  REACT_APP__VERSION,
  REACT_APP__VERSION_RELOAD_ATTEMPTS,
  storeEp,
} from "@smp/utils";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { useEffectOnce } from "react-use";
import semverLt from "semver/functions/lt";

import { useContextLanguage } from "@/context/language";
import { ELocale } from "@/context/language/language.types";
import { useEpConfig } from "@/hooks/use_ep_config";

import { DialogPageReload } from "./dialog_page_reload";
import { ErrorLoading } from "./error_loading";
import { IContextLoader } from "./loader.types";

const ContextLoader = createContext<IContextLoader>(
  null as unknown as IContextLoader,
);

export const ProviderLoader = ({ children }: PropsWithChildren<{}>) => {
  const [errorOrFailure, setErrorOrFailure] = useState<Error | undefined>(
    undefined,
  );
  const [isVersionActual, setIsVersionActual] = useState(true);

  const { config, configFailureApi } = useEpConfig({
    refetchInterval: REACT_APP__INTERVAL_VERSION_CHECK,
  });

  const { changeLanguage } = useContextLanguage();

  const skipVersionUpdate = () => {
    setIsVersionActual(true);
  };

  const ensureVersionActual = (
    versionCurrent: string,
    forceReload: boolean,
    versionTarget?: string,
  ) => {
    if (!versionTarget) {
      setIsVersionActual(true);
      return;
    }
    if (forceReload && versionCurrent !== versionTarget) {
      const attemptsDone = storeEp.get("reload_attempts") || 0;
      if (attemptsDone >= REACT_APP__VERSION_RELOAD_ATTEMPTS) {
        setErrorOrFailure(new Error("Too many reload attempts"));
      } else {
        storeEp.set("reload_attempts", attemptsDone + 1);
        setTimeout(() => {
          window.location.reload();
        }, (attemptsDone + 1) * 1000 * 2);
      }
    } else if (!forceReload && semverLt(versionCurrent, versionTarget)) {
      setIsVersionActual(false);
    } else {
      storeEp.set("reload_attempts", 0);
      setIsVersionActual(true);
    }
  };

  /**
   * Check if version actual or not on config or version update
   */

  useEffect(() => {
    if (!config || !REACT_APP__VERSION) return;
    ensureVersionActual(
      REACT_APP__VERSION,
      config.client.force_reload,
      config.client.version,
    );
  }, [config]);

  /**
   * Check if url includes `aid` and store it if true
   * Import language catalogs
   */

  useEffectOnce(() => {
    (async () => {
      const aid = new URLSearchParams(window.location.search).get("aid");
      const frc = new URLSearchParams(window.location.search).get("frc");
      if (frc) {
        storeEp.set("frc", frc);
        const frcDecoded = hashRot13(frc);
        const frcObject = frcDecoded
          .split(";")
          .reduce((accumulator, keyValueString) => {
            const [key, value] = keyValueString.split("=");
            accumulator[key] = value;
            return accumulator;
          }, {} as Record<string, string>);
        if (frcObject.lng === ELocale.ru || frcObject.lng === ELocale.en) {
          changeLanguage(frcObject.lng);
        }
      }
      if (aid) {
        storeEp.set("aid", aid);
      }
    })();
  });

  return (
    <>
      {configFailureApi || errorOrFailure ? (
        <ErrorLoading
          errorOrFailure={(configFailureApi as FailureApi) || errorOrFailure}
        />
      ) : config ? (
        <ContextLoader.Provider
          value={{
            state: {
              config,
            },
          }}
        >
          {children}
          {!isVersionActual && config.client.version && (
            <DialogPageReload
              skip={skipVersionUpdate}
              versionTarget={config.client.version}
            />
          )}
        </ContextLoader.Provider>
      ) : (
        <UiModuleLoader />
      )}
    </>
  );
};

export const useContextLoader = () => useContext(ContextLoader);
